// === Public API Surface ===

// Config
export {
	defineConfig,
	loadConfig,
} from './config/index.js'
export type { VoleConfig, LoopConfig, HeartbeatConfig, RateLimits } from './config/index.js'

// Core
export { createMessageBus } from './core/bus.js'
export type { MessageBus, BusEvents } from './core/bus.js'
export { runAgentLoop } from './core/loop.js'
export type { LoopDependencies } from './core/loop.js'
export { TaskQueue } from './core/task.js'
export type { AgentTask, TaskStatus, TaskPriority } from './core/task.js'
export { SchedulerStore } from './core/scheduler.js'
export { ContextBudgetManager } from './core/context-budget.js'
export type { TokenBudget } from './core/context-budget.js'
export { CostTracker } from './core/cost-tracker.js'
export type { CostEntry, TaskCostSummary } from './core/cost-tracker.js'
export type { AgentProfile, DockerSandboxConfig } from './config/index.js'
export { DockerSandboxManager } from './paw/docker-sandbox.js'
export { VoleHubClient } from './skill/volehub.js'
export type { VoleHubSkill, VoleHubIndex } from './skill/volehub.js'
export { VoleNetManager } from './net/index.js'
// Identity and trust primitives, for anything that runs a node of its own (the VoleNet MCP
// server, tests, tooling) rather than reaching for the CLI.
export {
	generateKeyPair,
	loadAuthorizedVoles,
	loadKeyPair,
	parsePublicKey,
	revokePeer,
	trustPeer,
} from './net/keys.js'
export type { VoleNetConfig } from './net/index.js'
export { AgentManager } from './agent/manager.js'
export type { AgentEntry, AgentRegistry, AgentStatus, AgentRunState } from './agent/types.js'
// Deprecated pre-rename aliases ("space" era) — remove in a future major.
export { AgentManager as SpaceManager } from './agent/manager.js'
export type {
	AgentEntry as SpaceEntry,
	AgentRegistry as SpaceRegistry,
	AgentStatus as SpaceStatus,
	AgentRunState as SpaceRunState,
} from './agent/types.js'
export { listChannels, channelIdFor, pickSendTool } from './channel/registry.js'
export type { ChannelInfo } from './channel/registry.js'
export { buildSystemPrompt, loadSystemPromptContent } from './core/system-prompt.js'
export {
	CONTEXT_NAME,
	MANIFEST_NAME,
	ProjectStore,
	RESERVED_BASENAMES,
	TASKS_NAME,
	isValidProjectId,
	validateProjectRoot,
} from './project/store.js'
export { TaskStore } from './project/tasks.js'
export { ProjectFileError, ProjectFiles } from './project/files.js'
export type { FileContent, FileEntry, FileListing, FileRoot, FileRootKey } from './project/files.js'
export { listProjectRoster, narrowToolAccess, resolveProjectContext } from './project/context.js'
export {
	ProjectError,
	ProjectRootError,
	TASK_TRANSITIONS,
	TERMINAL_TASK_STATES,
	type ProjectContextInfo,
	type ProjectKind,
	type ProjectManifest,
	type ProjectStatus,
	type ProjectTask,
	type TaskBudget,
	type TaskState,
} from './project/types.js'
export type { SystemPromptContent } from './core/system-prompt.js'
export { Vault } from './core/vault.js'
export type { VaultEntry } from './core/vault.js'
export { PHASE_ORDER } from './core/hooks.js'
export type { LoopPhase } from './core/hooks.js'
export { RateLimiter } from './core/rate-limiter.js'

// Errors
export {
	createActionError,
	successResult,
	failureResult,
} from './core/errors.js'
export type { ActionError, ActionResult, ActionErrorCode } from './core/errors.js'

// Tool
export { ToolRegistry } from './tool/registry.js'
export type { ToolDefinition, ToolRegistryEntry } from './tool/types.js'

// Context
export { createAgentContext } from './context/types.js'
export type {
	AgentContext,
	AgentMessage,
	ToolSummary,
	ActiveSkill,
} from './context/types.js'

// Paw
export { PawRegistry } from './paw/registry.js'
export { readPawManifest, resolvePawPath } from './paw/manifest.js'
export {
	computeEffectivePermissions,
	validatePermissions,
} from './paw/sandbox.js'
export type {
	PawDefinition,
	PawManifest,
	PawConfig,
	PawInstance,
	AgentPlan,
	PlannedAction,
	BootstrapHook,
	PerceiveHook,
	ObserveHook,
	CompactHook,
	ScheduleHook,
	TransportType,
	EffectivePermissions,
} from './paw/types.js'

// Skill
export { SkillRegistry } from './skill/registry.js'
export { resolveSkills, buildActiveSkills } from './skill/resolver.js'
export type {
	SkillDefinition,
	SkillInstance,
} from './skill/types.js'

// IO
export { createTtyIO } from './io/tty.js'
export type { VoleIO } from './io/types.js'

// === Engine — the main orchestrator ===

import * as fs from 'node:fs/promises'
import * as path from 'node:path'
import { type VoleConfig, loadConfig, normalizePawConfig } from './config/index.js'
import { createMessageBus } from './core/bus.js'
import { closeLogger } from './core/logger.js'
import { runAgentLoop } from './core/loop.js'
import { RateLimiter } from './core/rate-limiter.js'
import { SchedulerStore } from './core/scheduler.js'
import { type SystemPromptContent, loadSystemPromptContent } from './core/system-prompt.js'
import { type AgentTask, TaskQueue } from './core/task.js'
import { Vault } from './core/vault.js'
import { createTtyIO } from './io/tty.js'
import type { VoleIO } from './io/types.js'
import { peekPawCategory } from './paw/manifest.js'
import { PawRegistry } from './paw/registry.js'
import { listProjectRoster, resolveProjectContext } from './project/context.js'
import { ProjectStore } from './project/store.js'
import { TaskStore } from './project/tasks.js'
import type { ProjectContextInfo } from './project/types.js'
import { SkillRegistry } from './skill/registry.js'
import { createCoreTools } from './tool/core-tools.js'
import { ToolRegistry } from './tool/registry.js'

export interface VoleEngine {
	bus: ReturnType<typeof createMessageBus>
	toolRegistry: ToolRegistry
	pawRegistry: PawRegistry
	skillRegistry: SkillRegistry
	taskQueue: TaskQueue
	scheduler: SchedulerStore
	io: VoleIO
	config: VoleConfig
	/** Projects in this agent's workspace. */
	projects: ProjectStore
	/** Durable work items inside those projects (distinct from the in-memory TaskQueue). */
	projectTasks: TaskStore

	/** Start the engine — load Paws and Skills */
	start(): Promise<void>
	/** Submit a task for execution. Returns the task id. */
	run(
		input: string,
		source?: 'user' | 'schedule' | 'heartbeat' | 'paw' | 'agent',
		sessionId?: string,
		/** Task metadata — notably `projectId`/`projectTaskId` to scope the run to a project. */
		metadata?: Record<string, unknown>,
	): string
	/** Graceful shutdown */
	shutdown(): Promise<void>
}

import { createLogger } from './core/logger.js'

const engineLogger = createLogger('openvole')

/** Create and initialize the OpenVole engine */
/**
 * Turn `heartbeat.intervalMinutes` into a valid cron expression.
 *
 * A naive minutes-step pattern breaks the moment the interval reaches 60: the minutes field
 * only accepts steps up to 59, croner throws, and the engine dies at startup — so an hourly
 * (60) or daily (1440) heartbeat used to brick the agent. Map onto the right field instead:
 * under an hour steps minutes; an hour or more steps hours; a day or more runs once daily
 * (cron cannot express "every n days").
 */
export function heartbeatCronFor(intervalMinutes: number): string {
	const m = Math.max(1, Math.floor(intervalMinutes || 0))
	if (m < 60) return `*/${m} * * * *`
	const hours = Math.round(m / 60)
	// cron has no "every n days" — anything a day or longer runs once daily at midnight.
	if (hours >= 24) return '0 0 * * *'
	return `0 */${hours} * * *`
}

export async function createEngine(
	projectRoot: string,
	options?: { io?: VoleIO; configPath?: string; headless?: boolean },
): Promise<VoleEngine> {
	const configPath = options?.configPath ?? path.resolve(projectRoot, 'vole.config.ts')
	const config = await loadConfig(configPath)

	const bus = createMessageBus()
	const toolRegistry = new ToolRegistry(bus)
	const pawRegistry = new PawRegistry(bus, toolRegistry, projectRoot)
	const skillRegistry = new SkillRegistry(bus, toolRegistry, projectRoot)
	const io = options?.io ?? createTtyIO()
	const rateLimiter = new RateLimiter()
	const taskQueue = new TaskQueue(
		bus,
		config.loop.taskConcurrency,
		rateLimiter,
		config.loop.rateLimits,
	)
	const scheduler = new SchedulerStore()
	scheduler.setPersistence(path.resolve(projectRoot, '.openvole', 'schedules.json'))
	scheduler.setTickHandler((input, opts) => {
		taskQueue.enqueue(input, 'schedule', {
			...(opts?.projectId ? { metadata: { projectId: opts.projectId } } : {}),
		})
	})
	const vault = new Vault(
		path.resolve(projectRoot, '.openvole', 'vault.json'),
		process.env.VOLE_VAULT_KEY,
	)
	await vault.init()

	// Projects live in the workspace the agent already writes to; allowedPaths is what bounds an
	// external project root, so the store is the only thing that needs to know about it.
	const projects = new ProjectStore(path.resolve(projectRoot, '.openvole', 'workspace'), {
		agentRoot: projectRoot,
		allowedPaths: config.security?.allowedPaths,
	})
	await projects.init()
	const projectTasks = new TaskStore(projects)

	/** Resolve a task's project scope — see project/context.ts for why scope travels with the task. */
	const resolveProject = async (task: AgentTask): Promise<ProjectContextInfo | null> =>
		resolveProjectContext(projects, projectTasks, task.metadata, {
			// Self-initiated runs have no instruction of their own, so they pull the project's
			// next queued work. A user's chat message IS the instruction and must not be
			// overridden by whatever happens to sit at the top of the queue.
			autoSelectTask: task.source === 'schedule' || task.source === 'heartbeat',
		})

	// Register built-in core tools
	const coreTools = createCoreTools(
		scheduler,
		taskQueue,
		projectRoot,
		skillRegistry,
		vault,
		toolRegistry,
		bus,
		{ projects, tasks: projectTasks },
	)
	toolRegistry.register('__core__', coreTools, true)

	// Enable Tool Horizon if configured
	if (config.loop.toolHorizon) {
		toolRegistry.setHorizon(true)
		// Memory, session, and compact paws are always visible (infrastructure)
		toolRegistry.addAlwaysVisiblePaw('@openvole/paw-memory')
		toolRegistry.addAlwaysVisiblePaw('@openvole/paw-session')
		toolRegistry.addAlwaysVisiblePaw('@openvole/paw-compact')
	}

	// Wire up query sources so Paws can query skills and tasks
	pawRegistry.setQuerySources(skillRegistry, taskQueue, scheduler)
	pawRegistry.setSecurity(config.security)

	// System prompt content — loaded on start(), used by the loop
	let promptContent: SystemPromptContent | undefined

	// Wire up the task runner
	taskQueue.setRunner(async (task) => {
		// Check if we should delegate to a remote brain
		const voleNet = (globalThis as any).__volenet__
		if (voleNet?.isActive()) {
			const targetBrain = voleNet.shouldDelegateBrain()
			if (targetBrain) {
				const remoteMgr = voleNet.getRemoteTaskManager()
				if (remoteMgr) {
					engineLogger.info(`Delegating task to remote brain: ${targetBrain.substring(0, 8)}`)
					const result = await remoteMgr.delegateTask(targetBrain, {
						taskId: task.id,
						input: task.input,
						maxIterations: config.loop.maxIterations,
					})
					task.result = result.result ?? result.error ?? 'Remote task completed'
					if (result.status === 'failed') task.error = result.error ?? 'Remote task failed'
					if (task.source === 'user') io.notify(task.result ?? 'Done')
					return
				}
			}
		}

		await runAgentLoop(task, {
			bus,
			toolRegistry,
			pawRegistry,
			skillRegistry,
			io,
			config: config.loop,
			toolProfiles: config.toolProfiles,
			rateLimiter,
			systemPromptContent: promptContent,
			resolveProject,
			listProjects: () => listProjectRoster(projects, projectTasks),
		})
	})

	let shuttingDown = false

	const engine: VoleEngine = {
		bus,
		toolRegistry,
		pawRegistry,
		skillRegistry,
		taskQueue,
		scheduler,
		io,
		config,
		projects,
		projectTasks,

		async start() {
			engineLogger.info('Starting OpenVole...')
			const headless = options?.headless ?? false

			// Set Brain (setBrain resolves config path → manifest name after load)
			if (config.brain) {
				// setBrain is called after brain paw loads (below) so the mapping exists
			} else {
				engineLogger.info('No Brain Paw configured — Think step will be a no-op')
			}

			// In headless mode there is no human attached, so channel Paws are skipped: they open
			// listeners and pollers to reach somebody who isn't there. Decided by the manifest's
			// `category: "channel"` rather than a hardcoded name list, so any channel Paw — including
			// ones that don't exist yet — is covered. The dashboard Paw is named explicitly because it
			// is categorised as infrastructure.
			const headlessSkipPatterns = ['paw-dashboard']
			const headlessSkip = async (configName: string): Promise<boolean> => {
				if (headlessSkipPatterns.some((pat) => configName.includes(pat))) return true
				return (await peekPawCategory(configName, projectRoot)) === 'channel'
			}

			// Load Paws (Brain first, then others, in-process last)
			const pawConfigs = config.paws.map(normalizePawConfig)
			const brainConfig = pawConfigs.find((p) => p.name === config.brain)
			const subprocessPaws = pawConfigs.filter((p) => p.name !== config.brain)

			// Load Brain Paw first
			if (brainConfig) {
				const ok = await pawRegistry.load(brainConfig)
				if (ok) {
					pawRegistry.setBrain(config.brain!)
					engineLogger.info(`Brain Paw: ${pawRegistry.resolveManifestName(config.brain!)}`)
				} else {
					engineLogger.error(
						`Brain Paw "${brainConfig.name}" failed to load — running in no-op Think mode`,
					)
					pawRegistry.setBrain('')
				}
			}

			// Load system prompt content (BRAIN.md + identity files)
			const brainManifestName = config.brain
				? pawRegistry.resolveManifestName(config.brain)
				: undefined
			promptContent = await loadSystemPromptContent(projectRoot, brainManifestName)

			// Load other Paws in parallel
			let pawsToLoad = subprocessPaws
			if (headless) {
				const keep = await Promise.all(
					subprocessPaws.map(async (p) => !(await headlessSkip(p.name))),
				)
				pawsToLoad = subprocessPaws.filter((_, i) => keep[i])
			}
			await Promise.all(pawsToLoad.map((pawConfig) => pawRegistry.load(pawConfig)))

			// Channels stay reachable under tool horizon. A hidden channel is an unusable one: the
			// system prompt tells the agent it can message its human, so the send tool must be
			// callable without a discover_tools round-trip first.
			if (config.loop.toolHorizon) {
				for (const paw of pawRegistry.list()) {
					if (paw.manifest?.category === 'channel') {
						toolRegistry.addAlwaysVisiblePaw(paw.name)
					}
				}
			}

			// The brain may be referenced by manifest name while its paws entry uses a local
			// path (or vice versa) — resolve again now that manifest names are known. Without
			// this, think() silently no-ops and every task completes with no response.
			if (config.brain && !brainConfig) {
				pawRegistry.setBrain(config.brain)
				const resolved = pawRegistry.getBrainName()
				if (resolved && pawRegistry.get(resolved)) {
					engineLogger.info(`Brain Paw: ${resolved}`)
				} else {
					engineLogger.error(
						`Brain Paw "${config.brain}" not found among loaded paws — running in no-op Think mode`,
					)
				}
			}

			// Load Skills
			for (const skillName of config.skills) {
				await skillRegistry.load(skillName)
			}

			// Final resolver pass
			skillRegistry.resolve()

			// Restore persisted schedules from disk (skipped in headless mode)
			if (!headless) {
				await scheduler.restore()
			} else {
				// In headless mode, use loadFromDisk for read-only access (no persistence)
				await scheduler.loadFromDisk()
			}

			// Start heartbeat via scheduler (skip in headless mode)
			if (config.heartbeat.enabled && !headless) {
				const heartbeatMdPath = path.resolve(projectRoot, '.openvole', 'HEARTBEAT.md')
				// An explicit cron wins; else derive one from intervalMinutes.
				const heartbeatCron =
					config.heartbeat.cron?.trim() || heartbeatCronFor(config.heartbeat.intervalMinutes)
				// A heartbeat is a convenience, not the agent: an unparseable schedule must never
				// take the whole engine down at startup (scheduler.add throws on a bad pattern).
				try {
					scheduler.add(
						'__heartbeat__',
						'Heartbeat wake-up',
						heartbeatCron,
						async () => {
							let heartbeatContent = ''
							try {
								heartbeatContent = await fs.readFile(heartbeatMdPath, 'utf-8')
							} catch {
								// No HEARTBEAT.md — use default prompt
							}

							// The heartbeat is the recurring trigger, so it is what turns a queued task
							// into work actually being done. Without this a task sits in `queued`
							// forever unless a human asks for it in chat, which defeats the point of a
							// queue. HEARTBEAT.md jobs still run: both go in, and the agent decides.
							const queued = await projectTasks.next().catch(() => null)

							const parts: string[] = []
							if (heartbeatContent) {
								parts.push(
									`Review your HEARTBEAT.md jobs and act on what is needed:\n\n${heartbeatContent}`,
								)
							}
							if (queued) {
								parts.push(
									`You have queued project work. The next task is "${queued.goal}" in project "${queued.projectId}" — its context and done-criteria are in your Current Project section. Mark it running with task_update before you start, check the criteria yourself when you think it is finished, and only then mark it done. If a criterion does not hold, mark it blocked with the reason instead.`,
								)
							}
							if (parts.length === 0) {
								parts.push('Check active skills and decide if any actions are needed.')
							}

							taskQueue.enqueue(
								`Heartbeat wake-up. ${parts.join('\n\n')}`,
								'heartbeat',
								queued
									? { metadata: { projectId: queued.projectId, projectTaskId: queued.id } }
									: undefined,
							)
						},
						undefined,
						config.heartbeat.runOnStart ?? false,
					)
					engineLogger.info(
						`Heartbeat enabled — cron: ${heartbeatCron}${config.heartbeat.runOnStart ? ', running now' : ''}`,
					)
				} catch (err) {
					engineLogger.error(
						`Heartbeat disabled — invalid schedule "${heartbeatCron}": ${err instanceof Error ? err.message : String(err)}. The agent runs normally; fix heartbeat.cron / heartbeat.intervalMinutes in vole.config.json.`,
					)
				}
			}

			// Start VoleNet if configured
			const netConfig = (config as any).net as import('./net/index.js').VoleNetConfig | undefined
			if (netConfig?.enabled) {
				;(globalThis as any).__volenet_taskqueue__ = taskQueue
				try {
					const { VoleNetManager, persistPeerTo, persistPeerEntryTo } = await import(
						'./net/index.js'
					)
					// The library says *when* a peer was learned; writing it into vole.config.json is
					// ours, since only we know that file's shape.
					const voleNet = new VoleNetManager(
						{
							persistPeer: persistPeerTo(projectRoot),
							persistPeerEntry: persistPeerEntryTo(projectRoot),
							...netConfig,
						},
						projectRoot,
					)
					await voleNet.start(toolRegistry, bus)
					;(engine as any).__volenet__ = voleNet
					engineLogger.info(`VoleNet active — ${voleNet.getInstances().length} peer(s) connected`)
				} catch (err) {
					engineLogger.error(
						`VoleNet failed to start: ${err instanceof Error ? err.message : String(err)}`,
					)
				}
			}

			engineLogger.info(
				`Ready — ${toolRegistry.list().length} tools, ` +
					`${pawRegistry.list().length} paws, ` +
					`${skillRegistry.active().length}/${skillRegistry.list().length} skills active`,
			)
		},

		run(input, source = 'user', sessionId?, metadata?) {
			return taskQueue.enqueue(input, source, {
				...(sessionId ? { sessionId } : {}),
				...(metadata ? { metadata } : {}),
			}).id
		},

		async shutdown() {
			if (shuttingDown) return
			shuttingDown = true
			engineLogger.info('Shutting down...')
			// Stop VoleNet
			const voleNet = (engine as any).__volenet__
			if (voleNet) {
				await voleNet.stop()
			}
			scheduler.clearAll()
			taskQueue.cancelAll()
			await Promise.all(pawRegistry.list().map((paw) => pawRegistry.unload(paw.name)))
			toolRegistry.clear()
			engineLogger.info('Shutdown complete')
			closeLogger()
		},
	}

	return engine
}
